import { type ReactNode, useCallback, useMemo } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import classNames from 'classnames';
import { tooltipContainerClassName } from './BarChart';
import useScreenSize from '../../hooks/useScreenSize';
import { getDateAxisTicks } from '../../utils/charts';
import { formatWithNumeral } from '../../utils/numbers';
import LoadingSpinner from '../LoadingSpinner';

export type AreaChartDatum = {
  x: Date;
  y: number;
};

export type AreaChartSeries = {
  id: string;
  data: AreaChartDatum[];
  color: string;
};

type StackedPoint = { x: Date; y0: number; y1: number };

export type AreaChartProps = {
  /** Single-series mode */
  data?: AreaChartDatum[];
  color?: string;
  /** Multi-series mode — overrides data/color when provided */
  series?: AreaChartSeries[];
  margin?: { top: number; right: number; bottom: number; left: number };
  xTickFormat?: (value: Date | number) => string;
  xTickValues?: Date[];
  yTickFormat?: (value: number | string) => string;
  /** Receives the hovered datum. In multi-series mode y is the total stack height at that x. */
  renderTooltip: (datum: AreaChartDatum) => ReactNode;
  gridStroke?: string;
  axisTickColor?: string;
  tickFontSize?: number;
};

const DEFAULT_MARGIN = { top: 20, right: 20, bottom: 50, left: 62 };

const AreaChartInner = ({
  width,
  height,
  data,
  color = '#46DA93',
  series: seriesProp,
  margin = DEFAULT_MARGIN,
  xTickFormat = (value: Date | number) => {
    if (value instanceof Date) {
      return value.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
    return String(value);
  },
  xTickValues: xTickValuesProp,
  yTickFormat = (value) => String(formatWithNumeral(value, false, true)),
  renderTooltip,
  gridStroke = '#424242',
  axisTickColor = '#909090',
  tickFontSize = 12,
}: AreaChartProps & { width: number; height: number }) => {
  const { isMobile } = useScreenSize();

  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<AreaChartDatum>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const allSeries: AreaChartSeries[] = useMemo(
    () => seriesProp ?? [{ id: 'default', data: data ?? [], color }],
    [seriesProp, data, color],
  );

  // Compute stacked data for multi-series: each series gets cumulative y0/y1
  const stackedData: StackedPoint[][] | null = useMemo(() => {
    if (allSeries.length <= 1) return null;
    return allSeries.map((s, seriesIdx) =>
      s.data.map((d, dateIdx) => {
        const y0 = allSeries
          .slice(0, seriesIdx)
          .reduce((sum, prev) => sum + (prev.data[dateIdx]?.y ?? 0), 0);
        return { x: d.x, y0, y1: y0 + d.y };
      }),
    );
  }, [allSeries]);

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const xScale = useMemo(() => {
    const timestamps = allSeries.flatMap((s) => s.data.map((d) => d.x.getTime()));
    return scaleTime<number>({
      domain: timestamps.length ? [Math.min(...timestamps), Math.max(...timestamps)] : [0, 1],
      range: [0, xMax],
    });
  }, [allSeries, xMax]);

  const yScale = useMemo(() => {
    let maxY: number;
    if (stackedData) {
      const topSeries = stackedData.at(-1);
      maxY = topSeries ? Math.max(...topSeries.map((d) => d.y1), 0) : 0;
    } else {
      maxY = allSeries.length
        ? Math.max(...allSeries.flatMap((s) => s.data.map((d) => d.y)), 0)
        : 0;
    }
    return scaleLinear<number>({
      domain: [0, maxY * 1.2 || 1],
      range: [yMax, 0],
      nice: true,
    });
  }, [allSeries, stackedData, yMax]);

  const referenceData = allSeries[0]?.data ?? [];

  const xTickValues = useMemo(
    () => xTickValuesProp ?? getDateAxisTicks(referenceData.map((d) => d.x)),
    [xTickValuesProp, referenceData],
  );

  const yTickCount = useMemo(() => {
    const tickLength = yScale.ticks(5).length;
    return tickLength > 5 ? 5 : tickLength;
  }, [yScale]);

  const hoveredIdx = useMemo(() => {
    if (!tooltipData) return null;
    const idx = referenceData.findIndex((d) => d.x.getTime() === tooltipData.x.getTime());
    return idx >= 0 ? idx : null;
  }, [tooltipData, referenceData]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const coords = localPoint(event);
      if (!coords || !referenceData.length) return;
      const xMs = xScale.invert(coords.x - margin.left).getTime();
      let closestIdx = 0;
      let minDiff = Infinity;
      for (let i = 0; i < referenceData.length; i += 1) {
        const diff = Math.abs(referenceData[i].x.getTime() - xMs);
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = i;
        }
      }
      const closestX = referenceData[closestIdx].x;
      const totalY = allSeries.reduce((sum, s) => sum + (s.data[closestIdx]?.y ?? 0), 0);
      showTooltip({
        tooltipLeft: xScale(closestX.getTime()) + margin.left,
        tooltipTop: coords.y,
        tooltipData: { x: closestX, y: totalY },
      });
    },
    [referenceData, allSeries, xScale, margin, showTooltip],
  );

  if (width < 10 || height < 10) return null;

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={containerRef} width={width} height={height} role="application">
        {!stackedData && (
          <defs>
            {allSeries.map((s, i) => (
              <linearGradient key={s.id} id={`area-gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={1} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0.4} />
              </linearGradient>
            ))}
          </defs>
        )}
        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={xMax}
            numTicks={yTickCount}
            stroke={gridStroke}
            strokeWidth={1}
            strokeDasharray="2"
          />
          {stackedData
            ? stackedData.map((sData, i) => {
                const s = allSeries[i];
                return (
                  <g key={s.id}>
                    <AreaClosed
                      data={sData}
                      x={(d) => xScale(d.x.getTime())}
                      y={(d) => yScale(d.y1)}
                      y0={(d) => yScale(d.y0)}
                      yScale={yScale}
                      fill={s.color}
                      fillOpacity={0.3}
                      curve={curveMonotoneX}
                    />
                    <LinePath
                      data={sData}
                      x={(d) => xScale(d.x.getTime())}
                      y={(d) => yScale(d.y1)}
                      stroke={s.color}
                      strokeWidth={1}
                      curve={curveMonotoneX}
                    />
                  </g>
                );
              })
            : allSeries.map((s, i) => (
                <g key={s.id}>
                  <AreaClosed
                    data={s.data}
                    x={(d) => xScale(d.x.getTime())}
                    y={(d) => yScale(d.y)}
                    yScale={yScale}
                    fill={`url(#area-gradient-${i})`}
                    fillOpacity={0.4}
                    curve={curveMonotoneX}
                  />
                  <LinePath
                    data={s.data}
                    x={(d) => xScale(d.x.getTime())}
                    y={(d) => yScale(d.y)}
                    stroke={s.color}
                    strokeWidth={1}
                    curve={curveMonotoneX}
                  />
                </g>
              ))}
          {tooltipData && (
            <line
              x1={xScale(tooltipData.x.getTime())}
              x2={xScale(tooltipData.x.getTime())}
              y1={0}
              y2={yMax}
              stroke="#FFFFFF"
              strokeOpacity={0.4}
              strokeWidth={1}
              strokeDasharray="2"
              style={{ pointerEvents: 'none' }}
            />
          )}
          {hoveredIdx !== null &&
            (stackedData
              ? stackedData.map((sData, i) => {
                  const s = allSeries[i];
                  const point = sData[hoveredIdx];
                  if (!point) return null;
                  const cx = xScale(point.x.getTime());
                  const cy = yScale(point.y1);
                  return (
                    <g key={s.id} style={{ pointerEvents: 'none' }}>
                      <circle cx={cx} cy={cy} r={6} fill={s.color} fillOpacity={0.2} />
                      <circle cx={cx} cy={cy} r={3} fill={s.color} />
                    </g>
                  );
                })
              : allSeries.map((s) => {
                  const point = s.data[hoveredIdx];
                  if (!point) return null;
                  const cx = xScale(point.x.getTime());
                  const cy = yScale(point.y);
                  return (
                    <g key={s.id} style={{ pointerEvents: 'none' }}>
                      <circle cx={cx} cy={cy} r={6} fill={s.color} fillOpacity={0.2} />
                      <circle cx={cx} cy={cy} r={3} fill={s.color} />
                    </g>
                  );
                }))}
          {/* Hover detection overlay — rendered last so it captures all mouse events */}
          <rect
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={hideTooltip}
          />
          <AxisBottom
            top={yMax}
            scale={xScale}
            tickValues={xTickValues}
            tickFormat={(d) => xTickFormat(d as Date | number)}
            stroke="transparent"
            tickStroke="transparent"
            tickLength={0}
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: tickFontSize,
              textAnchor: 'end',
              angle: isMobile ? -60 : 0,
              dy: isMobile ? '-0.10em' : '0.50em',
              dx: isMobile ? '0.33em' : '1.2em',
            }}
          />
          <AxisLeft
            scale={yScale}
            numTicks={yTickCount}
            hideZero
            tickFormat={(value) => yTickFormat(value as number)}
            stroke="transparent"
            tickStroke="transparent"
            tickLength={0}
            tickLabelProps={{
              fill: axisTickColor,
              fontFamily: 'Aeonik-Medium',
              fontSize: tickFontSize,
              textAnchor: 'end',
              dx: isMobile ? '-0.33em' : '-0.50em',
              dy: '0.33em',
            }}
          />
        </Group>
      </svg>
      {tooltipOpen &&
        tooltipData &&
        (isMobile ? (
          <div className="pointer-events-none fixed inset-0 z-10 flex items-center justify-center">
            <div className={tooltipContainerClassName}>{renderTooltip(tooltipData)}</div>
          </div>
        ) : (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            unstyled
            applyPositionStyle
            className="pointer-events-none z-10"
          >
            <div className={tooltipContainerClassName}>{renderTooltip(tooltipData)}</div>
          </TooltipInPortal>
        ))}
    </div>
  );
};

export const AreaChart = ({
  isLoading,
  className = 'h-[350px]',
  ...props
}: AreaChartProps & { isLoading: boolean; className?: string }) => (
  <div className={classNames(className, 'relative')}>
    {isLoading ? (
      <>
        <img
          src="/bar-chart-placeholder.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50 blur-sm"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </>
    ) : (
      <ParentSize>
        {({ width, height }) => <AreaChartInner {...props} width={width} height={height} />}
      </ParentSize>
    )}
  </div>
);
